import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Settings_Module,
  AI_Entity,
  AuthPages,
  Entity,
  Entity_Label,
  FolderType,
  ModulePath,
  Super_Admin_Module,
  USER_ROLE,
} from "../constant/ApplicationVariables";
import dayjs from "dayjs";
import { debounce } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { useRef } from "react";
import { getDashboardReportList_API } from "../api/controller/DashboardController";
import { HttpStatusCode } from "axios";
import {
  getLabelList_API,
  addLabel_API,
} from "../api/controller/SettingsController";
import useErrorHandler from "../api/errorHandler/useErrorHandler";

export const ApplicationContext = createContext();
let EXPIRATION_THRESHOLD = 5; // 5 minutes in milliseconds

const IDLE_TIME_BEFORE_POPUP = 10 * 60 * 1000; // 10 minutes
const POPUP_VISIBLE_TIME = 10 * 60 * 1000; // 10 minutes

export const ApplicationContextProvider = ({ children }) => {
  const pathName = useLocation()?.pathname;
  const navigate = useNavigate();
  const { t } = useTranslation("translation");

  // Add these with your other refs
  const idleTimerRef = useRef(null);
  const popupTimerRef = useRef(null);
  const isPopupOpenRef = useRef(false);

  // All About Label
  //   const [labelList, setLabelList] = useState([]);
  const [aiProvider, setAiProvider] = useState("llama"); // To store the AI provider
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [labelAnchorEl, setLabelAnchorEl] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [openLabelDialog, setOpenLabelDialog] = useState(false);
  const [isLabelEditMode, setIsLabelEditMode] = useState(false);
  const [currentLabelData, setCurrentLabelData] = useState(null);
  const [labelData, setLabelData] = useState({ name: "", color: "#FF5733" });

  const [toggleChatWindow, setToggleChatWindow] = useState(false); // To toggle the AI chat window
  const [toggleFolderSidebar, setToggleFolderSidebar] = useState(true); // To toggle the folder sidebar
  const [isItemChatWindow, setIsItemChatWindow] = useState(false); // To toggle the AI chat window on item click
  const [isAIWindowMaximized, setIsAIWindowMaximized] = useState(true); // Flag to track if the AI window is maximized

  const [sidePanelSize, setSidePanelSize] = useState(24); // Default size for the side panel
  const [AIPanelSize, setAIPanelSize] = useState(26); // Default size for the AI panel
  const [treeData, setTreeData] = useState([]);

  const [showLicenseDialog, setShowLicenseDialog] = useState(false); // To show the license dialog
  const [showValidTillConfirmation, setShowValidTillConfirmation] =
    useState(false); // To show the token expiration confirmation
  const [intervalId, setIntervalId] = useState(null); // Token expiration interval
  // const [expirationTimerId, setExpirationTimerId] = useState(null);
  const expirationTimerId = useRef(null);
  const [timeoutId, setTimeoutId] = useState(null); // Renamed from intervalId
  const [showTokenExpired, setShowTokenExpired] = useState(false); // To show the token expired message

  const [globalSnackbar, setGLobalSnackBar] = useState({
    open: false,
    title: "",
    severity: "",
  }); // To show the global snackbar
  const [globalLoader, setGlobalLoader] = useState(false); // To show the global loader

  const [headerSearchText, setHeaderSearchText] = useState(""); // To store the header search text
  const [moduleSearchText, setModuleSearchText] = useState(""); // To store the module search text

  const [profileDetails, setProfileDetails] = useState(null); // To store the profile details of the user with token
  const [userDetail, setUserDetail] = useState(null); // To store the complete profile details of the user without token
  const [userLicenseDetail, setUserLicenseDetail] = useState(null); // To store the user license detail
  const [userPermissionsList, setUserPermissionsList] = useState([]); // To store the user permissions list
  const [accountVerificationType, setAccountVerificationType] = useState(""); // To store the account verification type / otp verification

  const [labelList, setLabelList] = useState([]); // To store the labels list of the user

  const [currentAIEntityData, setCurrentAIEntityData] = useState(null); // To store the current AI entity, label data, data = { label: "", entity: "" }
  const [currentAIResponseSaveData, setCurrentAIResponseSaveData] =
    useState(null); // To store the current AI response save data
  const [currentAIContextData, setCurrentAIContextData] = useState(null); // To store the current AI context data

  // Inside ApplicationContext.js
  const fetchDashboardReports = async (projectName) => {
    try {
      const response = await getDashboardReportList_API({ projectName });
      if (response.status === HttpStatusCode.Ok) {
        setDashboardReportList(response.data);
        return response.data;
      }
    } catch (error) {}
  };

  /* toggle states */
  const [openCreateOrganisationDialog, setOpenCreateOrganisationDialog] =
    useState(false); // To open the create organisation dialog
  const [openEditOrganisationDialog, setOpenEditOrganisationDialog] =
    useState(false); // To open the edit organisation dialog
  const [openDeleteOrganisationDialog, setOpenDeleteOrganisationDialog] =
    useState(false); // To open the delete organisation dialog

  const [openCreateLicenseDialog, setOpenCreateLicenseDialog] = useState(false); // To open the create license dialog
  const [openEditLicenseDialog, setOpenEditLicenseDialog] = useState(false); // To open the edit license dialog
  const [openDeleteLicenseDialog, setOpenDeleteLicenseDialog] = useState(false); // To open the delete license dialog

  const [openCreateInvoiceDialog, setOpenCreateInvoiceDialog] = useState(false); // To open the create invoice dialog
  const [openEditInvoiceDialog, setOpenEditInvoiceDialog] = useState(false); // To open the edit invoice dialog
  const [openDeleteInvoiceDialog, setOpenDeleteInvoiceDialog] = useState(false); // To open the delete invoice dialog

  const [openCreateUserDialog, setOpenCreateUserDialog] = useState(false); // To open the create user dialog

  const [openCreateRoleDialog, setOpenCreateRoleDialog] = useState(false); // To open the create role dialog
  const [openEditRoleDialog, setOpenEditRoleDialog] = useState(false); // To open the edit role dialog
  const [openDeleteRoleDialog, setOpenDeleteRoleDialog] = useState(false); // To open the delete role dialog

  const [openCreateProgramDrawer, setOpenCreateProgramDrawer] = useState(false); // To open the create program drawer
  const [openEditProgramDrawer, setOpenEditProgramDrawer] = useState(false); // To open the edit program drawer
  const [openDeleteProgramDialog, setOpenDeleteProgramDialog] = useState(false); // To open the delete program dialog

  const [openCreateProjectDrawer, setOpenCreateProjectDrawer] = useState(false); // To open the create project drawer
  const [openEditProjectDrawer, setOpenEditProjectDrawer] = useState(false); // To open the edit project drawer
  const [openDeleteProjectDialog, setOpenDeleteProjectDialog] = useState(false); // To open the delete project dialog
  const [openItemFilterDrawer, setOpenItemFilterDrawer] = useState(false); // To open the item filter drawer

  const [openCreateFolderDialog, setOpenCreateFolderDialog] = useState(false); // To open the create folder dialog
  const [openEditFolderDialog, setOpenEditFolderDialog] = useState(false); // To open the edit folder dialog
  const [openDeleteFolderDialog, setOpenDeleteFolderDialog] = useState(false); // To open the delete folder dialog

  const [openCreateStoryDialog, setOpenCreateStoryDialog] = useState(false); // To open the create story dialog
  const [openEditStoryDialog, setOpenEditStoryDialog] = useState(false); // To open the edit story dialog
  const [openDeleteStoryDialog, setOpenDeleteStoryDialog] = useState(false); // To open the delete story dialog
  const [openStoryPreview, setOpenStoryPreview] = useState(false); // To open the story preview - NOT IN USE
  const [openStoryConfirmationPreview, setOpenStoryConfirmationPreview] =
    useState(false); // To open the story confirmation preview - OLD
  const [openLinkTagDrawer, setOpenLinkTagDrawer] = useState(false); // To open the link tag drawer

  const [openCreateUseCaseDialog, setOpenCreateUseCaseDialog] = useState(false); // To open the create usecase dialog
  const [openEditUseCaseDialog, setOpenEditUseCaseDialog] = useState(false); // To open the edit usecase dialog
  const [openDeleteUseCaseDialog, setOpenDeleteUseCaseDialog] = useState(false); // To open the delete usecase dialog

  const [openCreateReleaseDialog, setOpenCreateReleaseDialog] = useState(false); // To open the create release dialog
  const [openCreateSprintDialog, setOpenCreateSprintDialog] = useState(false); // To open the create sprint dialog

  const [openCreateTestSetDialog, setOpenCreateTestSetDialog] = useState(false); // To open the create testset dialog

  const [openCreateReportDialog, setOpenCreateReportDialog] = useState(false); // To open the create report dialog
  const [openEditReportDialog, setOpenEditReportDialog] = useState(false); // To open the edit report dialog
  const [openDeleteReportDialog, setOpenDeleteReportDialog] = useState(false); // To open the delete report dialog

  const [openCreateGraphDialog, setOpenCreateGraphDialog] = useState(false); // To open the create graph dialog
  const [openEditGraphDialog, setOpenEditGraphDialog] = useState(false); // To open the edit graph dialog
  const [openDeleteGraphDialog, setOpenDeleteGraphDialog] = useState(false); // To open the delete graph dialog
  const [openGraphPreview, setOpenGraphPreview] = useState(false); // To open the graph preview dialog

  const [openCreateRiskDialog, setOpenCreateRiskDialog] = useState(false); // To open the create risk dialog
  const [openEditRiskDialog, setOpenEditRiskDialog] = useState(false); // To open the edit risk dialog
  const [openDeleteRiskDialog, setOpenDeleteRiskDialog] = useState(false); // To open the delete risk dialog
  const [openCreateIssueDialog, setOpenCreateIssueDialog] = useState(false); // To open the create issue dialog
  const [openEditIssueDialog, setOpenEditIssueDialog] = useState(false); // To open the edit issue dialog
  const [openDeleteIssueDialog, setOpenDeleteIssueDialog] = useState(false); // To open the delete issue dialog
  const [openCreateDependencyDialog, setOpenCreateDependencyDialog] =
    useState(false); // To open the create dependency dialog
  const [openEditDependencyDialog, setOpenEditDependencyDialog] =
    useState(false); // To open the edit dependency dialog
  const [openDeleteDependencyDialog, setOpenDeleteDependencyDialog] =
    useState(false); // To open the delete dependency dialog
  const [openCreateDecisionDialog, setOpenCreateDecisionDialog] =
    useState(false); // To open the create decision dialog
  const [openEditDecisionDialog, setOpenEditDecisionDialog] = useState(false); // To open the edit decision dialog
  const [openDeleteDecisionDialog, setOpenDeleteDecisionDialog] =
    useState(false); // To open the delete decision dialog
  const [openCreateActionDialog, setOpenCreateActionDialog] = useState(false); // To open the create action dialog
  const [openEditActionDialog, setOpenEditActionDialog] = useState(false); // To open the edit action dialog
  const [openDeleteActionDialog, setOpenDeleteActionDialog] = useState(false); // To open the delete action dialog
  const [openCreateTeamDialog, setOpenCreateTeamDialog] = useState(false); // To open the create team dialog
  const [openEditTeamDialog, setOpenEditTeamDialog] = useState(false); // To open the edit team dialog

  const [openCreateRegressionDialog, setOpenCreateRegressionDialog] =
    useState(false); // To open the create regression dialog
  const [openEditRegressionDialog, setOpenEditRegressionDialog] =
    useState(false); // To open the edit regression dialog
  const [openDeleteRegressionDialog, setOpenDeleteRegressionDialog] =
    useState(false); // To open the delete regression dialog

  const [openCreateTaskDialog, setOpenCreateTaskDialog] = useState(false); // To open the create task dialog
  const [openEditTaskDialog, setOpenEditTaskDialog] = useState(false); // To open the edit task dialog
  const [openDeleteTaskDialog, setOpenDeleteTaskDialog] = useState(false); // To open the delete task dialog

  /* list and data states*/
  const [activeSuperAdminModule, setActiveSuperAdminModule] = useState(
    Super_Admin_Module.organisation,
  ); // To store the active super admin module

  const [organisationList, setOrganisationList] = useState([]); // To store the organisation list
  const [currentOrganisationData, setCurrentOrganisationData] = useState(null); // To store the current organisation data

  const [licenseList, setLicenseList] = useState([]); // To store the license list
  const [currentLicenseData, setCurrentLicenseData] = useState(null); // To store the current license data

  const [invoicesList, setInvoicesList] = useState([]); // To store the invoices list
  const [currentInvoiceData, setCurrentInvoiceData] = useState(null); // To store the current invoice data

  const [activeSettingsModule, setActiveSettingsModule] = useState(
    Settings_Module.dashboard,
  ); // To store the active admin module

  const [rolesList, setRolesList] = useState([]); // To store the roles list
  const [currentRoleData, setCurrentRoleData] = useState(null); // To store the current role data

  const [programList, setProgramList] = useState([]); // To store the program list
  const [currentProgramData, setCurrentProgramData] = useState(null); // To store the current program data

  const [activeProject, setActiveProject] = useState(null); // To store the active project data

  // Initial state
  const [recentProjectsList, setRecentProjectsList] = useState(() => {
    const stored = localStorage.getItem("recentProjects");
    if (stored) {
      // Keep only last 5 projects (minimal info)
      const parsed = JSON.parse(stored);
      return parsed.slice(0, 5).map((p) => ({ id: p.id, name: p.name }));
    }
    return [];
  });

  const handleRecentProjectSelect = (project) => {
    const projectNameOnly = { name: project.name };

    setActiveProject(project);

    setRecentProjectsList((prevList) => {
      // Remove duplicates
      const filtered = prevList.filter((p) => p.name !== projectNameOnly.name);

      // Add new project to the top
      const updatedList = [projectNameOnly, ...filtered];

      // Keep only max 5
      const finalList = updatedList.slice(0, 5);

      // Save to localStorage
      localStorage.setItem("recentProjects", JSON.stringify(finalList));

      return finalList; // this updates the state immediately
    });

    // Optionally save the active project
    localStorage.setItem("activeProject", JSON.stringify(project));
  };

  const [categoryList, setCategoryList] = useState([]); // To store the category list
  const [subCategoryList, setSubCategoryList] = useState([]); // To store the sub category list
  const [activeProjectCategory, setActiveProjectCategory] = useState(null); // To store the active project category
  const [activeProjectSubcategory, setActiveProjectSubcategory] =
    useState(null); // To store the active project subcategory
  const [projectList, setProjectList] = useState([]); // To store the project list
  const [isUpdateProjectList, setIsUpdateProjectList] = useState(false); // Toggle to update the project list

  const treeRef = useRef(null);
  const [isFolderStructureLoading, setIsFolderStructureLoading] =
    useState(false); // Toggle to show the folder structure loading
  const [folderStructureList, setFolderStructureList] = useState([]); // To store the folder structure list
  const [currentFolderData, setCurrentFolderData] = useState(null); // To store the current folder data
  const [currentDataSavingFolder, setCurrentDataSavingFolder] = useState(null); // To store the current data saving folder data
  const [currentFolderAnchorCoordinates, setCurrentFolderAnchorCoordinates] =
    useState(null); // To store the current folder anchor coordinates
  const [isUpdateFolderStructure, setIsUpdateFolderStructure] = useState(false); // Toggle to update the folder structure
  const [isUpdateCurrentFolderData, setIsUpdateCurrentFolderData] =
    useState(false); // Toggle to update the current folder data
  const [currentEditFolderData, setCurrentEditFolderData] = useState(null); // To store the current folder edit data
  const [newFolderLocation, setNewFolderLocation] = useState(""); // to Store the new / updated folder path
  const [folderSearchTerm, setFolderSearchTerm] = useState(""); // To Store the folder search term

  const [userStoryList, setUserStoryList] = useState([]); // To store the user story list
  const [currentUserStoryData, setCurrentUserStoryData] = useState(null); // To store the current user story
  const [currentUserStory, setCurrentUserStory] = useState(null); // To store the current user story - OLD
  const [selectedUserStoryList, setSelectedUserStoryList] = useState([]); // To store the selected user story
  const [isUserStoryLoading, setIsUserStoryLoading] = useState(false); // To show the user story loading
  const [filteredUserStoryList, setFilteredUserStoryList] = useState([]); // To store the filtered user story list
  const [userStoryPropertiesData, setUserStoryPropertiesData] = useState(null); // To store the user story properties  (type, state, moscow, priority, confidentiality)

  const [useCaseList, setUseCaseList] = useState([]); // To store the use case list
  const [currentUseCaseData, setCurrentUseCaseData] = useState(null); // To store the current use case
  const [selectedUseCaseList, setSelectedUseCaseList] = useState([]); // To store the selected use case
  const [filteredUseCaseList, setFilteredUseCaseList] = useState([]); // To store the filtered use case list
  const [useCasePropertiesData, setUseCasePropertiesData] = useState(null); // To store the use case properties data (type, priority, state, status)

  const [testCaseList, setTestCaseList] = useState([]); // To store the test case list
  const [currentTestCaseData, setCurrentTestCaseData] = useState(null); // To store the current test case
  const [selectedTestCaseList, setSelectedTestCaseList] = useState([]); // To store the selected test case
  const [filteredTestCaseList, setFilteredTestCaseList] = useState([]); // To store the filtered test case list

  const [dashboardReportList, setDashboardReportList] = useState([]); // To store the dashboard report list
  const [currentDashboardReportData, setCurrentDashboardReportData] =
    useState(null); // To store the current dashboard report

  const [activeDashboard, setActiveDashboard] = useState(null); // To store the active dashboard
  const [activeDashboardModule, setActiveDashboardModule] =
    useState("overview"); // To store the active dashboard module
  const [dashboardItemsList, setDashboardItemsList] = useState([]); // particular property data list : []
  const [currentGraphData, setCurrentGraphData] = useState(null); // current graph data
  const [raidList, setRaidList] = useState([]); // To store the RAID list (Risks, Issues, Dependencies, Decisions)
  const [currentRaidData, setCurrentRaidData] = useState(null); // To store the current RAID data
  const [currentRiskData, setCurrentRiskData] = useState(null); // To store the current risk data
  const [currentIssueData, setCurrentIssueData] = useState(null); // To store the current issue data
  const [currentDependencyData, setCurrentDependencyData] = useState(null); // To store the current dependency data
  const [currentDecisionData, setCurrentDecisionData] = useState(null); // To store the current decision data
  const [teamsList, setTeamsList] = useState([]); // To store the teams list
  const [currentTeamsData, setCurrentTeamsData] = useState(null); // To store the current teams item data

  const [currentReleaseData, setCurrentReleaseData] = useState(null); // To store the current release data
  const [currentSprintData, setCurrentSprintData] = useState(null); // To store the current sprint data

  const [currentTestSetData, setCurrentTestSetData] = useState(null); // To store the current test set data

  const [currentExecutionTestCaseData, setCurrentExecutionTestCaseData] =
    useState(null); // To store the current execution test case data
  const [testExecutionList, setTestExecutionList] = useState([]); // To store the test execution list
  const [selectedTestExecutionList, setSelectedTestExecutionList] = useState(
    [],
  ); // To store the selected test execution list
  const [openTestExecuteTemplateDialog, setOpenTestExecuteTemplateDialog] =
    useState(false); // To open the test execute template dialog
  const [
    openTestAutomationExecuteTemplateDialog,
    setOpenTestAutomationExecuteTemplateDialog,
  ] = useState(false); // To open the test execute Automation template dialog

  const [regressionList, setRegressionList] = useState([]); // To store the regression list
  const [currentRegressionData, setCurrentRegressionData] = useState(null); // To store the current regression data

  const [taskList, setTaskList] = useState([]); // To store the task list
  const [currentTaskData, setCurrentTaskData] = useState(null); // To store the current task data

  const [activeConfiguration, setActiveConfiguration] = useState(null); // To store the active configuration

  /* loading and filter states*/
  const [isActiveProjectContainerLoading, setIsActiveProjectContainerLoading] =
    useState(false); // To show the active project container loading
  const [currentFilters, setCurrentFilters] = useState(null); // To store the current filters of active project items list

  const [isContentLoading, setIsContentLoading] = useState(false); // To show the content loading
  const [isGridViewEnabled, setIsGridViewEnabled] = useState(false); // To enable the grid view
  const [isShowStoryFilter, setIsShowStoryFilter] = useState(true); // To show the story filter

  const [dashboardFilterSelectedRelease, setDashboardFilterSelectedRelease] =
    useState("");
  const [dashboardFilterSelectedSprint, setDashboardFilterSelectedSprint] =
    useState("");

  const [selectedReleaseIndex, setSelectedReleaseIndex] = useState(null);
  const [selectedSprintIndex, setSelectedSprintIndex] = useState(null);

  // All Label functions
  const handleToggleLabelPopup = (e) => {
    e && e.stopPropagation();
    setLabelAnchorEl(labelAnchorEl ? null : e.currentTarget);
  };

  const handleChangeLabel = (label) => {
    let list = [...selectedLabels];
    const index = list.findIndex((item) => item.name === label.name);
    if (index > -1) list.splice(index, 1);
    else list.push(label);
    setSelectedLabels(list);
  };

  const handleGetList = async (activeProject) => {
    try {
      setLoading(true);
      const query = new URLSearchParams({
        name: activeProject?.name,
      }).toString();
      const response = await getLabelList_API(query);
      if (response.status === 200) setLabelList(response.data);
      else {
        // Show error using global snackbar
        setGLobalSnackBar({
          open: true,
          title: response?.data?.message || "Failed to fetch labels",
          severity: "error",
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLabel = async (activeProject, name) => {
    try {
      setLoading(true);
      const response = await addLabel_API({
        name,
        projectName: activeProject?.name,
      });
      if (response.status === 200) {
        handleGetList(activeProject);
      } else {
        setGLobalSnackBar({
          open: true,
          title: response?.data?.message || "Failed to add label",
          severity: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLabelUpdate = (callback) => {
    if (callback) callback(selectedLabels);
    setLabelAnchorEl(null);
    setSearchText("");
  };
  const handleOpenLabelAddUpdate_Dialog = (isEdit, item) => {
    setIsLabelEditMode(isEdit);
    setOpenLabelDialog(true);

    if (item && isEdit) {
      setCurrentLabelData(item);
      setLabelData({ name: item?.name, color: item?.colorCode });
    }
  };
  const handleCloseDialog = () => {
    setOpenLabelDialog(false);
    setIsLabelEditMode(false);
    setCurrentLabelData(null);
    setLabelData({ name: "", color: "#FF5733" });
  };
  const handleClickLabelButton = () => {
    setActiveConfiguration({ value: "Labels" });
    navigate(ModulePath.configuration);
  };

  // Update your existing handleSnackbar function to this:
  const handleSnackbar = (message, severity, setLoadingState) => {
    // Format message to ensure consistent display
    const formattedMessage = message || "";

    setGLobalSnackBar({
      open: true,
      title: formattedMessage,
      severity: severity || "info",
    });
    setLoadingState && setLoadingState(false);
  };

  const handleLogout = () => {
    // Clear session timers
    clearSessionTimers();
    isPopupOpenRef.current = false;

    localStorage.removeItem("profileDetails");
    localStorage.removeItem("access-token");
    localStorage.removeItem("activeProject");
    localStorage.clear();

    navigate(ModulePath.login);
    setProfileDetails(null);
    setUserDetail(null);
    setActiveProject(null);
    setProjectList([]);
    setRolesList([]);
    setUserStoryList([]);
    setFolderStructureList([]);
    setUserStoryList([]);
  };

  // Session Timeout Management Functions
  const clearSessionTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (popupTimerRef.current) {
      clearTimeout(popupTimerRef.current);
      popupTimerRef.current = null;
    }
  }, []);

  const resetIdleTimer = useCallback(() => {
    // Don't reset if popup is already open
    if (showValidTillConfirmation || isPopupOpenRef.current) return;

    clearSessionTimers();

    idleTimerRef.current = setTimeout(() => {
      // Show popup after 10 minutes of inactivity
      isPopupOpenRef.current = true;
      setShowValidTillConfirmation(true);

      // Auto logout after another 10 minutes if no response
      popupTimerRef.current = setTimeout(() => {
        isPopupOpenRef.current = false;
        setShowValidTillConfirmation(false);
        handleLogout();
      }, POPUP_VISIBLE_TIME);
    }, IDLE_TIME_BEFORE_POPUP);
  }, [showValidTillConfirmation, clearSessionTimers, handleLogout]);

  const extendSessionAndReset = useCallback(() => {
    clearSessionTimers();
    isPopupOpenRef.current = false;
    setShowValidTillConfirmation(false);
    resetIdleTimer();
  }, [clearSessionTimers, resetIdleTimer]);

  const handleStoreUserData = (responseData) => {
    localStorage.setItem("access-token", responseData?.authToken);
    localStorage.setItem("profileDetails", JSON.stringify(responseData));
    setProfileDetails(responseData);
  };

  const getAILabel = () => {
    if (currentAIEntityData) {
      // return `AI assistance for: ${currentAIEntityData?.label}`;
      return `${t("ai.labelText")}: ${currentAIEntityData?.label}`;
      // switch (currentFolderData?.type) {
      //     case FolderType.project: return `AI assistance for: ${currentAIEntityData?.label}`;
      //     case FolderType.module: return `AI assistance for: ${currentAIEntityData?.label}`;
      //     default: return "using AI";
      // }
    }
  };

  const handleRedirectAfterAuthentication = (role) => {
    console.log(role, "rolllle");
    if (role) {
      if (role?.toLowerCase() === USER_ROLE.superadmin?.toLowerCase()) {
        navigate(ModulePath.superAdmin);
      } else {
        navigate(ModulePath.projects);
      }
    } else {
      navigate(ModulePath.projects);
    }
  };

  // Track user activity
  useEffect(() => {
    // Only track activity if user is logged in and not on auth pages
    if (!profileDetails || AuthPages.includes(pathName)) return;

    const events = [
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "mousemove",
      "click",
    ];

    const handleUserActivity = () => {
      resetIdleTimer();
    };

    events.forEach((event) => {
      window.addEventListener(event, handleUserActivity);
    });

    resetIdleTimer(); // Start timer

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
      clearSessionTimers();
    };
  }, [profileDetails, pathName, resetIdleTimer, clearSessionTimers]);

  const handlePageDetails = useCallback(() => {
    switch (pathName) {
      case ModulePath.superAdmin: {
        const superAdminModuleDetails = {
          [Super_Admin_Module.organisation]: {
            title: Super_Admin_Module.organisation,
            description: "Manage and organise organisation details",
          },
          [Super_Admin_Module.license]: {
            title: Super_Admin_Module.license,
            description: "Manage and organise licenses",
          },
          [Super_Admin_Module.licenseAddon]: {
            title: Super_Admin_Module.licenseAddon,
            description: "Manage and organise license addons",
          },
          [Super_Admin_Module.invoices]: {
            title: Super_Admin_Module.invoices,
            description: "Manage and organise invoices",
          },
        };
        return superAdminModuleDetails[activeSuperAdminModule];
      }
      case ModulePath.settings: {
        const adminModuleDetails = {
          [Settings_Module.dashboard]: {
            title: Settings_Module.dashboard,
            description: "Manage and organise admin dashboard",
          },
          [Settings_Module.license]: {
            title: Settings_Module.license,
            description: "Manage and organise license details",
          },
          [Settings_Module.userManagement]: {
            title: Settings_Module.userManagement,
            description: "Manage and organise user management",
          },
          [Settings_Module.roleManagement]: {
            title: Settings_Module.roleManagement,
            description: "Manage and organise role management",
          },
          [Settings_Module.profile]: {
            title: Settings_Module.profile,
            description: "Manage and organise user profile",
          },
          [Settings_Module.activityLog]: {
            title: Settings_Module.activityLog,
            description: "Manage and organise activity log",
          },
          [Settings_Module.permissions]: {
            title: Settings_Module.permissions,
            description: "Manage and organise permissions",
          },
          [Settings_Module.teams]: {
            title: "Teams",
            description: "Manage and organise your teams",
          },
        };
        return adminModuleDetails[activeSettingsModule];
      }
      case ModulePath.projects:
        return {
          title: Entity_Label.project,
          description: "Manage and organise your project workspace",
        };
      case ModulePath.programmes:
        return {
          title: Entity_Label.programme,
          description: "Manage and organise your programmes",
        };
      case ModulePath.organisations:
        return {
          title: Entity_Label.organisation,
          description: "Manage and organise your organisations",
        };
      case ModulePath.requirements:
        return {
          title: Entity_Label.userStory,
          description: "Manage and organise your user stories",
        };
      case ModulePath.useCase:
        return {
          title: Entity_Label.useCase,
          description: "Manage and organise your use cases",
        };
      case ModulePath.testCase:
        return {
          title: Entity_Label.testCase,
          description: "Manage and organise your test cases",
        };
      case ModulePath.planning:
        return {
          title: Entity_Label.planning,
          description: "Manage and organise your planning",
        };
      case ModulePath.execution:
        return {
          title: Entity_Label.execution,
          description: "Manage and organise your execution",
        };
      case ModulePath.dashboard:
        return {
          title: Entity_Label.dashboard,
          description: "Manage and organise your dashboard",
        };
      case ModulePath.configuration:
        return {
          title: Entity_Label.configuration,
          description: "Manage and organise your project configurations",
        };
      case ModulePath.regression:
        return {
          title: Entity_Label.regression,
          description: "Manage and organise your regressions",
        };
      case ModulePath.task:
        return {
          title: Entity_Label.task,
          description: "Manage and organise your tasks",
        };
      default:
        return { title: "", description: "" };
    }
  }, [pathName, activeSettingsModule, activeSuperAdminModule]);

  const handleOpenItemInNewWindow = (item) => {
    if (item?.type) {
      if (item?.type === Entity.project && item?.name) {
        window.open(
          `${window.location.origin}${ModulePath.projects}?name=${item?.name}&type=${item?.type}`,
          "_blank",
        );
      }
    }

    if (item?.customId && item?.folderPath) {
      const basePath = (() => {
        switch (item.type) {
          case Entity.userStory:
            return ModulePath.requirements;
          case Entity.useCase:
            return ModulePath.useCase;
          case Entity.testCase:
            return ModulePath.testCase;
          default:
            return "";
        }
      })();

      window.open(
        `${window.location.origin}${basePath}?id=${item.customId}&path=${item.folderPath}`,
        "_blank",
      );
    }
  };

  // console.log(getAILabel());

  function hasPermission(module, action) {
    // Flatten the nested structure first
    function flattenPermissions(items) {
      let permissions = [];

      for (const item of items) {
        if (item.list && Array.isArray(item.list)) {
          permissions = permissions.concat(item.list);
        }

        if (item.child && Array.isArray(item.child)) {
          permissions = permissions.concat(flattenPermissions(item.child));
        }
      }

      return permissions;
    }

    const allPermissions = flattenPermissions(userPermissionsList);
    const permission = allPermissions.find(
      (p) => p.module === module && p.action === action,
    );

    return permission ? permission.enabled : false;
  }
  useEffect(() => {
    // Load recent projects from localStorage
    const storedRecent =
      JSON.parse(localStorage.getItem("recentProjects")) || [];
    setRecentProjectsList(storedRecent);

    // If activeProject exists, make sure it’s in recentProjectsList
    const storedActive = JSON.parse(localStorage.getItem("activeProject"));
    if (storedActive) {
      setActiveProject(storedActive);

      // Update recent projects if active project not already included
      const isInRecent = storedRecent.some((p) => p.name === storedActive.name);
      if (!isInRecent) {
        const updatedRecent = [
          { name: storedActive.name },
          ...storedRecent,
        ].slice(0, 5);
        setRecentProjectsList(updatedRecent);
        localStorage.setItem("recentProjects", JSON.stringify(updatedRecent));
      }
    }
  }, []);

  useEffect(() => {
    // when user authenticated
    if (window && localStorage.getItem("profileDetails")) {
      setProfileDetails(JSON.parse(localStorage.getItem("profileDetails")));
    }

    // when user is unauthenticated
    if (window && !localStorage.getItem("profileDetails")) {
      setProfileDetails(null);
    }

    // when user authenticated and active project is not set
    if (
      localStorage.getItem("activeProject") &&
      pathName !== ModulePath.projects &&
      !activeProject
    ) {
      setActiveProject(JSON.parse(localStorage.getItem("activeProject")));
    }

    // when user not authenticated
    // if (window && (!localStorage.getItem("profileDetails") || !localStorage.getItem("access-token"))) {
    //     navigate(ModulePath.login)
    // }
  }, [pathName]);

  useEffect(() => {
    if (
      activeProject &&
      folderStructureList?.length === 0 &&
      pathName !== ModulePath.projects
    ) {
      // setIsUpdateFolderStructure(true);
    }
  }, [activeProject, folderStructureList?.length]);

  // To set the currentAIEntityData based on the folder / item type
  useEffect(() => {
    if (!isItemChatWindow && currentAIEntityData?.entity !== AI_Entity.folder) {
      setCurrentAIEntityData({
        label:
          pathName === ModulePath.requirements
            ? Entity_Label.userStory
            : pathName === ModulePath.useCase
              ? Entity_Label.useCase
              : pathName === ModulePath.testCase
                ? Entity_Label.testCase
                : pathName === ModulePath.planning &&
                    currentFolderData?.type?.toLowerCase() ===
                      Entity.release?.toLowerCase()
                  ? Entity_Label.release
                  : pathName === ModulePath.planning &&
                      currentFolderData?.type?.toLowerCase() ===
                        Entity.sprint?.toLowerCase()
                    ? Entity_Label.sprint
                    : pathName === ModulePath.execution &&
                        currentFolderData?.type?.toLowerCase() ===
                          Entity.release?.toLowerCase()
                      ? Entity_Label.release
                      : pathName === ModulePath.execution &&
                          currentFolderData?.type?.toLowerCase() ===
                            Entity.testSet?.toLowerCase()
                        ? Entity_Label.testSet
                        : pathName === ModulePath.dashboard
                          ? Entity_Label.dashboard
                          : "",
        entity:
          pathName === ModulePath.requirements
            ? AI_Entity.userStory
            : pathName === ModulePath.useCase
              ? AI_Entity.useCase
              : pathName === ModulePath.testCase
                ? AI_Entity.testCase
                : pathName === ModulePath.planning &&
                    currentFolderData?.type?.toLowerCase() ===
                      Entity.release?.toLowerCase()
                  ? AI_Entity.release
                  : pathName === ModulePath.planning &&
                      currentFolderData?.type?.toLowerCase() ===
                        Entity.sprint?.toLowerCase()
                    ? AI_Entity.sprint
                    : pathName === ModulePath.execution &&
                        currentFolderData?.type?.toLowerCase() ===
                          Entity.release?.toLowerCase()
                      ? AI_Entity.release
                      : pathName === ModulePath.execution &&
                          currentFolderData?.type?.toLowerCase() ===
                            Entity.testSet?.toLowerCase()
                        ? AI_Entity.testSet
                        : pathName === ModulePath.dashboard
                          ? AI_Entity.dashboard
                          : "",
      });
    }
  }, [
    isItemChatWindow,
    pathName,
    currentFolderData?.id,
    toggleChatWindow,
    isAIWindowMaximized,
  ]);

  // In your ApplicationContext - replace the updateFolderCount function with this:
  const updateFolderCount = useCallback(
    (folderPath, countType, delta) => {
      console.log(`Updating ${countType} count for ${folderPath} by ${delta}`);

      // Helper function to recursively update node counts
      const updateNodeCount = (nodes, path, type, change) => {
        if (!nodes || !Array.isArray(nodes)) return nodes;

        return nodes.map((node) => {
          // Check if current node's path matches
          if (node.path === path) {
            const currentCount = node[`${type}Count`] || 0;
            const newCount = Math.max(0, currentCount + change);

            return {
              ...node,
              [`${type}Count`]: newCount,
            };
          }

          // Recursively check children
          if (node.children && node.children.length > 0) {
            return {
              ...node,
              children: updateNodeCount(node.children, path, type, change),
            };
          }

          return node;
        });
      };

      // Update both treeData and folderStructureList for consistency
      setTreeData((prev) =>
        updateNodeCount(prev, folderPath, countType, delta),
      );
      setFolderStructureList((prev) =>
        updateNodeCount(prev, folderPath, countType, delta),
      );
    },
    [setTreeData, setFolderStructureList],
  );

  // To reset the isMaximised state of AI Window
  useEffect(() => {
    if (!toggleChatWindow) {
      setIsAIWindowMaximized(false);
    }
  }, [toggleChatWindow, setIsAIWindowMaximized]);

  // Token Extend functionality
  useEffect(() => {
    const checkTokenExpiration = () => {
      const tokenValidTill = profileDetails?.validTill;
      if (!tokenValidTill) return;

      const warningTime = dayjs(tokenValidTill).subtract(
        EXPIRATION_THRESHOLD,
        "minutes",
      );
      const now = dayjs();
      const timeUntilWarning = warningTime.diff(now, "milliseconds");

      if (timeUntilWarning <= 0) {
        setShowValidTillConfirmation(true);
        clearTimeout(timeoutId);
        if (showTokenExpired) {
          setShowTokenExpired(false);
        }
      }
    };

    const startTimer = () => {
      clearTimeout(timeoutId); // Clear existing timeout first

      const tokenValidTill = profileDetails?.validTill;
      if (!tokenValidTill) return;

      const warningTime = dayjs(tokenValidTill).subtract(
        EXPIRATION_THRESHOLD,
        "minutes",
      );
      const now = dayjs();
      const timeUntilWarning = warningTime.diff(now, "milliseconds");

      if (timeUntilWarning > 0) {
        const id = setTimeout(() => {
          checkTokenExpiration(); // Actually call the function
        }, timeUntilWarning);
        setTimeoutId(id);
      } else {
        // checkTokenExpiration(); // Handle immediate case
        // setShowTokenExpired(true);
      }
    };

    // Debounced version with 1-second delay
    const debouncedStartTimer = debounce(startTimer, 1000);

    const handleUserInteraction = () => {
      if (profileDetails?.validTill) {
        debouncedStartTimer();
      }
    };

    // Setup
    const events = ["mousemove", "keydown", "scroll"];
    if (!AuthPages.includes(pathName)) {
      events.forEach((e) =>
        document.addEventListener(e, handleUserInteraction),
      );
      handleUserInteraction(); // Initial call
    } else {
      setShowValidTillConfirmation(false);
      setShowTokenExpired(false);
    }

    // Cleanup
    return () => {
      events.forEach((e) =>
        document.removeEventListener(e, handleUserInteraction),
      );
      clearTimeout(timeoutId);
    };
  }, [profileDetails, showValidTillConfirmation, pathName]);

  // useEffect(() => {
  //     const startTimer = () => {
  //         clearTimeout(expirationTimerId.current);

  //         const tokenValidTill = profileDetails?.validTill;
  //         if (!tokenValidTill || showValidTillConfirmation || AuthPages.includes(pathName)) return;

  //         const warningTime = dayjs(tokenValidTill).subtract(EXPIRATION_THRESHOLD, 'minutes');
  //         const timeUntilWarning = warningTime.diff(dayjs(), 'milliseconds');

  //         if (timeUntilWarning > 0) {
  //             expirationTimerId.current = setTimeout(() => {
  //                 setShowValidTillConfirmation(true);
  //             }, timeUntilWarning);
  //         } else {
  //             // setShowValidTillConfirmation(true);
  //         }
  //     };

  //     const handleUserInteraction = startTimer;

  //     // Setup
  //     const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'];
  //     events.forEach(e => document.addEventListener(e, handleUserInteraction));
  //     startTimer(); // Initial call

  //     // Cleanup
  //     return () => {
  //         events.forEach(e => document.removeEventListener(e, handleUserInteraction));
  //         clearTimeout(expirationTimerId.current);
  //     };
  // }, [profileDetails, showValidTillConfirmation, pathName]);
  //   const errorHandler = useErrorHandler();
  const values = {
    pathName,
    aiProvider,
    setAiProvider,
    // globaSnackBar,
    // setGLobalSnackBar,
    toggleChatWindow,
    setToggleChatWindow,
    toggleFolderSidebar,
    setToggleFolderSidebar,
    isItemChatWindow,
    setIsItemChatWindow,
    isAIWindowMaximized,
    setIsAIWindowMaximized,
    sidePanelSize,
    setSidePanelSize,
    AIPanelSize,
    setAIPanelSize,
    showLicenseDialog,
    setShowLicenseDialog,
    globalSnackbar,
    setGLobalSnackBar,
    globalLoader,
    setGlobalLoader,
    headerSearchText,
    setHeaderSearchText,
    moduleSearchText,
    setModuleSearchText,
    profileDetails,
    setProfileDetails,
    userDetail,
    setUserDetail,
    userLicenseDetail,
    setUserLicenseDetail,
    userPermissionsList,
    setUserPermissionsList,
    accountVerificationType,
    setAccountVerificationType,
    currentAIEntityData,
    setCurrentAIEntityData,
    currentAIResponseSaveData,
    setCurrentAIResponseSaveData,
    currentAIContextData,
    setCurrentAIContextData,
    openCreateOrganisationDialog,
    setOpenCreateOrganisationDialog,
    openEditOrganisationDialog,
    setOpenEditOrganisationDialog,
    openDeleteOrganisationDialog,
    setOpenDeleteOrganisationDialog,
    openCreateLicenseDialog,
    setOpenCreateLicenseDialog,
    openEditLicenseDialog,
    setOpenEditLicenseDialog,
    openDeleteLicenseDialog,
    setOpenDeleteLicenseDialog,
    openCreateInvoiceDialog,
    setOpenCreateInvoiceDialog,
    openEditInvoiceDialog,
    setOpenEditInvoiceDialog,
    openDeleteInvoiceDialog,
    setOpenDeleteInvoiceDialog,
    openCreateUserDialog,
    setOpenCreateUserDialog,
    openCreateRoleDialog,
    setOpenCreateRoleDialog,
    openEditRoleDialog,
    setOpenEditRoleDialog,
    openDeleteRoleDialog,
    setOpenDeleteRoleDialog,
    openCreateProgramDrawer,
    setOpenCreateProgramDrawer,
    openEditProgramDrawer,
    setOpenEditProgramDrawer,
    openDeleteProgramDialog,
    setOpenDeleteProgramDialog,
    showValidTillConfirmation,
    setShowValidTillConfirmation,
    showTokenExpired,
    setShowTokenExpired,
    openCreateProjectDrawer,
    setOpenCreateProjectDrawer,
    openEditProjectDrawer,
    setOpenEditProjectDrawer,
    openDeleteProjectDialog,
    setOpenDeleteProjectDialog,
    openItemFilterDrawer,
    setOpenItemFilterDrawer,
    openCreateFolderDialog,
    setOpenCreateFolderDialog,
    openEditFolderDialog,
    setOpenEditFolderDialog,
    openDeleteFolderDialog,
    setOpenDeleteFolderDialog,
    categoryList,
    setCategoryList,
    subCategoryList,
    setSubCategoryList,
    activeProjectCategory,
    setActiveProjectCategory,
    activeProjectSubcategory,
    setActiveProjectSubcategory,
    projectList,
    setProjectList,
    isUpdateProjectList,
    setIsUpdateProjectList,
    isFolderStructureLoading,
    setIsFolderStructureLoading,
    treeRef,
    currentUserStory,
    setCurrentUserStory,
    selectedUserStoryList,
    setSelectedUserStoryList,
    isUserStoryLoading,
    setIsUserStoryLoading,
    filteredUserStoryList,
    setFilteredUserStoryList,
    userStoryPropertiesData,
    setUserStoryPropertiesData,
    useCaseList,
    setUseCaseList,
    currentUseCaseData,
    setCurrentUseCaseData,
    selectedUseCaseList,
    setSelectedUseCaseList,
    filteredUseCaseList,
    setFilteredUseCaseList,
    useCasePropertiesData,
    setUseCasePropertiesData,
    testCaseList,
    setTestCaseList,
    currentTestCaseData,
    setCurrentTestCaseData,
    selectedTestCaseList,
    setSelectedTestCaseList,
    filteredTestCaseList,
    setFilteredTestCaseList,
    dashboardReportList,
    setDashboardReportList,
    currentDashboardReportData,
    setCurrentDashboardReportData,

    activeDashboard,
    setActiveDashboard,
    activeDashboardModule,
    setActiveDashboardModule,
    dashboardItemsList,
    setDashboardItemsList,
    currentGraphData,
    setCurrentGraphData,
    raidList,
    setRaidList,
    currentRaidData,
    setCurrentRaidData,
    currentRiskData,
    setCurrentRiskData,
    currentIssueData,
    setCurrentIssueData,
    currentDependencyData,
    setCurrentDependencyData,
    currentDecisionData,
    setCurrentDecisionData,
    teamsList,
    setTeamsList,
    currentTeamsData,
    setCurrentTeamsData,

    currentReleaseData,
    setCurrentReleaseData,
    currentSprintData,
    setCurrentSprintData,

    currentTestSetData,
    setCurrentTestSetData,

    currentExecutionTestCaseData,
    setCurrentExecutionTestCaseData,

    testExecutionList,
    setTestExecutionList,

    selectedTestExecutionList,
    setSelectedTestExecutionList,

    openTestExecuteTemplateDialog,
    setOpenTestExecuteTemplateDialog,

    openTestAutomationExecuteTemplateDialog,
    setOpenTestAutomationExecuteTemplateDialog,

    regressionList,
    setRegressionList,
    currentRegressionData,
    setCurrentRegressionData,

    taskList,
    setTaskList,
    currentTaskData,
    setCurrentTaskData,

    activeConfiguration,
    setActiveConfiguration,

    isActiveProjectContainerLoading,
    setIsActiveProjectContainerLoading,
    currentFilters,
    setCurrentFilters,

    folderStructureList,
    setFolderStructureList,
    userStoryList,
    setUserStoryList,
    currentFolderData,
    setCurrentFolderData,
    currentDataSavingFolder,
    setCurrentDataSavingFolder,
    currentFolderAnchorCoordinates,
    setCurrentFolderAnchorCoordinates,
    isUpdateFolderStructure,
    setIsUpdateFolderStructure,
    isUpdateCurrentFolderData,
    setIsUpdateCurrentFolderData,
    currentEditFolderData,
    setCurrentEditFolderData,
    newFolderLocation,
    setNewFolderLocation,
    folderSearchTerm,
    setFolderSearchTerm,
    currentUserStoryData,
    setCurrentUserStoryData,
    openCreateStoryDialog,
    setOpenCreateStoryDialog,
    openEditStoryDialog,
    setOpenEditStoryDialog,
    openDeleteStoryDialog,
    setOpenDeleteStoryDialog,
    openStoryPreview,
    setOpenStoryPreview,
    openStoryConfirmationPreview,
    setOpenStoryConfirmationPreview,
    openCreateUseCaseDialog,
    setOpenCreateUseCaseDialog,
    openEditUseCaseDialog,
    setOpenEditUseCaseDialog,
    openDeleteUseCaseDialog,
    setOpenDeleteUseCaseDialog,
    openCreateReleaseDialog,
    setOpenCreateReleaseDialog,
    openCreateSprintDialog,
    setOpenCreateSprintDialog,
    openCreateTestSetDialog,
    setOpenCreateTestSetDialog,
    openCreateReportDialog,
    setOpenCreateReportDialog,
    openEditReportDialog,
    setOpenEditReportDialog,
    openDeleteReportDialog,
    setOpenDeleteReportDialog,
    openCreateGraphDialog,
    setOpenCreateGraphDialog,
    openEditGraphDialog,
    setOpenEditGraphDialog,
    openDeleteGraphDialog,
    setOpenDeleteGraphDialog,
    openGraphPreview,
    setOpenGraphPreview,
    openCreateRiskDialog,
    setOpenCreateRiskDialog,
    openEditRiskDialog,
    setOpenEditRiskDialog,
    openDeleteRiskDialog,
    setOpenDeleteRiskDialog,
    openCreateIssueDialog,
    setOpenCreateIssueDialog,
    openEditIssueDialog,
    setOpenEditIssueDialog,
    openDeleteIssueDialog,
    setOpenDeleteIssueDialog,
    openCreateDependencyDialog,
    setOpenCreateDependencyDialog,
    openEditDependencyDialog,
    setOpenEditDependencyDialog,
    openDeleteDependencyDialog,
    setOpenDeleteDependencyDialog,
    openCreateDecisionDialog,
    setOpenCreateDecisionDialog,
    openEditDecisionDialog,
    setOpenEditDecisionDialog,
    openDeleteDecisionDialog,
    setOpenDeleteDecisionDialog,
    openCreateActionDialog,
    setOpenCreateActionDialog,
    openEditActionDialog,
    setOpenEditActionDialog,
    openDeleteActionDialog,
    setOpenDeleteActionDialog,
    openCreateTeamDialog,
    setOpenCreateTeamDialog,
    openEditTeamDialog,
    setOpenEditTeamDialog,
    openCreateRegressionDialog,
    setOpenCreateRegressionDialog,
    openEditRegressionDialog,
    setOpenEditRegressionDialog,
    openDeleteRegressionDialog,
    setOpenDeleteRegressionDialog,
    openCreateTaskDialog,
    setOpenCreateTaskDialog,
    openEditTaskDialog,
    setOpenEditTaskDialog,
    openDeleteTaskDialog,
    setOpenDeleteTaskDialog,
    activeSuperAdminModule,
    setActiveSuperAdminModule,
    organisationList,
    setOrganisationList,
    currentOrganisationData,
    setCurrentOrganisationData,
    licenseList,
    setLicenseList,
    currentLicenseData,
    setCurrentLicenseData,
    invoicesList,
    setInvoicesList,
    currentInvoiceData,
    setCurrentInvoiceData,
    activeSettingsModule,
    setActiveSettingsModule,
    rolesList,
    setRolesList,
    currentRoleData,
    setCurrentRoleData,
    programList,
    setProgramList,
    currentProgramData,
    setCurrentProgramData,
    openLinkTagDrawer,
    setOpenLinkTagDrawer,
    isContentLoading,
    setIsContentLoading,
    isGridViewEnabled,
    setIsGridViewEnabled,
    isShowStoryFilter,
    setIsShowStoryFilter,
    activeProject,
    setActiveProject,
    recentProjectsList,
    handleRecentProjectSelect,

    // label related
    labelList,
    setLabelList,
    selectedLabels,
    setSelectedLabels,
    labelAnchorEl,
    setLabelAnchorEl,
    searchText,
    setSearchText,
    loading,
    setLoading,
    openLabelDialog,
    setOpenLabelDialog,
    isLabelEditMode,
    setIsLabelEditMode,
    currentLabelData,
    setCurrentLabelData,
    labelData,
    setLabelData,
    // errorHandler,

    // functions for label menu card
    handleToggleLabelPopup,
    handleChangeLabel,
    handleOpenLabelAddUpdate_Dialog,
    handleLabelUpdate,
    handleAddLabel,
    handleCloseDialog,
    handleClickLabelButton,
    handleGetList,

    // functions
    handleSnackbar,
    handleLogout,
    handleStoreUserData,
    getAILabel,
    handleRedirectAfterAuthentication,
    handlePageDetails,
    handleOpenItemInNewWindow,
    hasPermission,
    updateFolderCount,

    dashboardFilterSelectedRelease,
    setDashboardFilterSelectedRelease,
    dashboardFilterSelectedSprint,
    setDashboardFilterSelectedSprint,
    selectedReleaseIndex,
    setSelectedReleaseIndex,
    selectedSprintIndex,
    setSelectedSprintIndex,
    extendSessionAndReset,
  };

  return (
    <ApplicationContext.Provider value={values}>
      {children}
    </ApplicationContext.Provider>
  );
};

export const useApplicationContext = () => useContext(ApplicationContext);
